import ChangeSizeButton from "@entrilia/shared/components/ChangeSizeButton.tsx";
import { Box, Stack, Tooltip } from "@mui/material";
import { Fragment, useCallback, useState } from "react";
import type {
  ConditionDescriptor,
  ConditionFilterDescriptor,
} from "../../../../../../../../api/types/fieldConfigurationTypes.ts";
import { ConditionEqualityType } from "../../../../../../../../api/types/fieldConfigurationTypes.ts";
import type { ConditionField } from "../../../../../../../../api/types/svTypes.ts";
import { ItemDataType } from "../../../../../../../../api/types/svTypes.ts";
import SwitchField from "../../../common/SwitchField.tsx";
import CustomLabel from "../fields/CustomLabel.tsx";
import FieldOptionDeleteButton from "../fields/FieldOptionDeleteButton.tsx";
import FieldOptionsDivider from "../fields/FieldOptionsDivider.tsx";
import FieldPopover from "../fields/FieldPopover.tsx";
import { isTextOperator } from "./conditionOperatorOptions.ts";
import DateFilter from "./DateFilter/DateFilter.tsx";
import ValueFilterCondition from "./ValueFilterCondition.tsx";

interface Props {
  anchorEl: HTMLElement;
  field: ConditionField;
  narrowingFilters: ConditionDescriptor[];
  showParameter?: boolean;
  readonlyCustomLabel?: boolean;
  showOnlySelectedByDefault?: boolean;
  readonly?: boolean;
  canBeRemoved?: boolean;
  resizable?: boolean;
  disableEqualitySelection?: boolean;
  disableReferenceDateSelection?: boolean;
  updateParameter?: (field: ConditionField, newValue: boolean) => void;
  updateMandatory?: (field: ConditionField, newValue: boolean) => void;
  updateCustomLabel?: (customLabel: string | undefined) => void;
  save: (filter: ConditionFilterDescriptor) => void;
  onRemoveItem?: (item: ConditionField) => void;
  CustomId: React.ReactNode;
}

export const ConditionFieldOptionPopup = ({
  anchorEl,
  field,
  narrowingFilters,
  showParameter,
  readonlyCustomLabel,
  showOnlySelectedByDefault,
  readonly,
  canBeRemoved,
  resizable,
  disableEqualitySelection,
  disableReferenceDateSelection,
  updateParameter,
  updateMandatory,
  updateCustomLabel,
  save,
  onRemoveItem,
  CustomId,
}: Props) => {
  const [newFilter, setNewFilter] = useState<ConditionFilterDescriptor>(() => {
    if (field.config.filter === undefined) {
      return {
        dimensionName: field.meta.name,
        values: [],
        equalityType: ConditionEqualityType.Equal,
      };
    }
    return structuredClone(field.config.filter);
  });
  const [isNewFilterValid, setIsNewFilterValid] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [editing, setEditing] = useState(false);

  const hasValueGrid = !isTextOperator(newFilter.equalityType);

  const handleDateFilterUpdated = useCallback((filter: ConditionFilterDescriptor, isValid: boolean) => {
    setNewFilter(filter);
    setIsNewFilterValid(isValid);
  }, []);

  const onBeforeClose = useCallback(() => {
    const filterToSave = isNewFilterValid
      ? newFilter
      : (field.config.filter ?? {
          dimensionName: field.meta.name,
          values: [],
          equalityType: ConditionEqualityType.Equal,
        });
    save(filterToSave);
  }, [newFilter, save, isNewFilterValid, field.config.filter, field.meta.name]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== "Enter" || event.defaultPrevented || editing) {
        return;
      }
      event.preventDefault();
      onBeforeClose();
    },
    [editing, onBeforeClose]
  );

  const updateAsParameter = useCallback(
    (useAsParameter: boolean) => {
      updateParameter?.call(this, field, useAsParameter);
    },
    [field, updateParameter]
  );

  const updateAsMandatory = useCallback(
    (useAsMandatory: boolean) => {
      updateMandatory?.call(this, field, useAsMandatory);
    },
    [field, updateMandatory]
  );

  const handleOnRemove = useCallback(() => {
    onRemoveItem?.call(null, field);
  }, [field, onRemoveItem]);

  return (
    <FieldPopover
      open={!!anchorEl}
      anchorEl={anchorEl}
      onClose={() => onBeforeClose()}
      onKeyDown={handleKeyDown}
      sx={{ display: "flex", flexDirection: "column", p: 0, ...getSizeParameters(isExpanded, hasValueGrid) }}
    >
      <CustomLabel
        customFieldValue={field.config.customLabel}
        originalFieldValue={field.meta.caption}
        updateCustomFieldValue={updateCustomLabel}
        readonly={readonlyCustomLabel ?? false}
        onEditMode={setEditing}
        sx={{ pt: 1 }}
      >
        {resizable && !editing && (
          <Tooltip
            key={+isExpanded}
            title={isExpanded ? "Collapse" : "Expand"}
            placement="bottom"
            arrow
            disableInteractive
          >
            <Box sx={{ display: "flex", marginLeft: "auto" }}>
              <ChangeSizeButton isExpanded={isExpanded} toggle={() => setIsExpanded((prev) => !prev)} />
            </Box>
          </Tooltip>
        )}
      </CustomLabel>
      {CustomId}
      <FieldOptionsDivider />
      {field.meta.type === ItemDataType.Date ? (
        <DateFilter
          field={field}
          onFilterUpdated={handleDateFilterUpdated}
          onReferenceDateSelected={save}
          disableEqualitySelection={disableEqualitySelection}
          disableReferenceDateSelection={disableReferenceDateSelection}
        />
      ) : (
        <ValueFilterCondition
          field={field}
          readonly={readonly}
          showOnlySelectedByDefault={showOnlySelectedByDefault}
          onFilterUpdated={setNewFilter}
          narrowingFilters={narrowingFilters}
        />
      )}
      {showParameter && [
        <FieldOptionsDivider key="dividerTop" />,
        <Stack key="menu-items" sx={{ py: 0.5, width: "100%" }}>
          <SwitchField
            key="parameter"
            label="Set as Parameter"
            checked={field.config.parameter || false}
            tooltipText="It provides the ability to edit the condition in running mode"
            onChange={() => updateAsParameter(!field.config.parameter)}
            containerSx={{ px: 2, py: 0.75 }}
          />
          {field.config.parameter && (
            <SwitchField
              key="mandatory"
              label="Set as Mandatory"
              checked={field.config.mandatory || false}
              onChange={() => updateAsMandatory(!field.config.mandatory)}
              containerSx={{ px: 2, py: 0.75 }}
            />
          )}
        </Stack>,
      ]}
      {canBeRemoved && !readonly && (
        <Fragment key={"RemoveItem"}>
          <FieldOptionsDivider />
          <FieldOptionDeleteButton onRemove={handleOnRemove} />
        </Fragment>
      )}
    </FieldPopover>
  );
};

export default ConditionFieldOptionPopup;

const expandedSize = {
  width: "600px",
  minHeight: "660px",
};
const collapsedSize = {
  width: "370px",
  height: "auto",
};

const getSizeParameters = (isExpanded: boolean, hasValueGrid: boolean) => {
  if (!isExpanded) {
    return collapsedSize;
  }
  // Only a value grid stretches to fill the expanded height; anything shorter would leave a blank gap.
  return hasValueGrid ? expandedSize : { width: expandedSize.width, height: collapsedSize.height };
};
